import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../contexts/ToastContext';
import { DEPT_DB_MAP, DEPARTMENTS, formatAmount, API, authFetch, LEVEL_BANDS, titleCase } from '../../constants';
import StatusDot from '../../components/StatusDot';
import AuditTrail from '../../components/AuditTrail';
import SILBreakdown from '../../components/SILBreakdown';
import ActionButton from '../../components/ui/ActionButton';
import { DetailRow } from './constants';

export default function ExceptionDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const [data, setData] = useState<any>(null);
  const [riskData, setRiskData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [resolution, setResolution] = useState<any>(null);
  const [loadingResolution, setLoadingResolution] = useState(false);
  const [executingOption, setExecutingOption] = useState<string | null>(null);
  const [resolveStatus, setResolveStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const { role, refreshData, authUser } = useApp();
  const { toast } = useToast();
  const currentUserId = authUser?.id || 1;

  const canApprove = (amount: number): boolean => {
    if (role.level === 'L6') return true;
    const band = LEVEL_BANDS[role.level];
    return band ? amount <= band.ceiling : false;
  };

  const getRequiredLevel = (amount: number): string => {
    if (amount <= 50000) return 'L2';
    if (amount <= 250000) return 'L3';
    if (amount <= 1000000) return 'L4';
    if (amount <= 5000000) return 'L5';
    return 'L6';
  };

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [detailRes, riskRes] = await Promise.all([
        authFetch(`${API}/${id}`),
        authFetch(`${API}/${id}/risk-score`),
      ]);
      if (detailRes.ok) {
        const d = await detailRes.json();
        setData(d);
        // Extract resolution from latest result
        const results = d.results || [];
        const latest = results.length > 0 ? results[results.length - 1] : null;
        if (latest?.recommendation_detail) {
          const rd = latest.recommendation_detail;
          if (rd.resolution_version || rd.options || rd.diagnosis) {
            setResolution(rd);
          }
        }
      }
      if (riskRes.ok) setRiskData(await riskRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const generateResolution = async () => {
    setLoadingResolution(true);
    try {
      const res = await authFetch(`${API}/${id}/generate-resolution`, { method: 'POST' });
      const rd = await res.json();
      if (rd.resolution_version || rd.diagnosis || rd.options) {
        setResolution(rd);
      }
    } catch (err) {
      console.error('Resolution generation failed:', err);
      toast({ message: 'Resolution generation failed. Please try again.', type: 'error' });
    }
    setLoadingResolution(false);
  };

  // Get the latest result ID for SOX audit trail
  const _results = data?.results || [];
  const _latestResult = _results.length > 0 ? _results[_results.length - 1] : null;
  const latestResultId = _latestResult?.id || null;

  const executeResolution = async (optionId: string, action: string, optionLabel?: string) => {
    setExecutingOption(optionId);
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${id}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          notes: `${action === 'approved' ? 'Approved' : 'Rejected'}: ${optionLabel || optionId}`,
          user_id: currentUserId,
          role_level: role.level,
          approval_limit: LEVEL_BANDS[role.level]?.ceiling || 0,
          resolution_summary: optionLabel || optionId,
          option_id: optionId,
          resolution_result_id: latestResultId,
        }),
      });
      if (res.ok) {
        setResolveStatus({ ok: true, msg: 'Exception resolved successfully' });
        await loadDetail();
        refreshData();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Resolution failed' });
      }
    } catch {
      setResolveStatus({ ok: false, msg: 'Network error' });
    }
    setExecutingOption(null);
  };

  const handleEscalate = async (level?: string) => {
    setResolveStatus(null);
    try {
      const url = level
        ? `${API}/${id}/escalate-to/${level}?reason=Escalated+from+detail+view`
        : `${API}/${id}/escalate`;
      const options: RequestInit = level
        ? { method: 'POST' }
        : { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: 'Escalated from detail view', user_id: currentUserId }) };
      const res = await authFetch(url, options);
      if (res.ok) {
        setResolveStatus({ ok: true, msg: level ? `Escalated to ${level}` : 'Escalated to next level' });
        await loadDetail();
        refreshData();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Escalation failed' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Escalation failed' });
      }
    } catch {
      setResolveStatus({ ok: false, msg: 'Network error' });
    }
  };

  if (loading) return <div style={{ padding: 80, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading exception #{id}...</div>;
  if (!data) return (
    <div style={{ padding: 80, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Exception #{id} not found</div>
      <ActionButton label="Back to Tracker" onClick={onBack} variant="secondary" />
    </div>
  );

  // Destructure the full API response
  const exc = data.exception || {};
  const results = data.results || [];
  const auditTrail = data.audit_trail || [];
  const confidenceBreakdown = data.confidence_breakdown;
  const signals = data.intelligence_signals || [];
  const resolutionAudit = data.resolution_audit;
  const latestResult = results.length > 0 ? results[results.length - 1] : null;

  // Derive display values
  const mappedDept = DEPT_DB_MAP[exc.department] || exc.department || 'Unknown';
  const deptColor = DEPARTMENTS[mappedDept]?.color || 'var(--text-tertiary)';
  const confidenceScore = confidenceBreakdown?.overall_score ?? exc.confidence;
  const diagnosis = latestResult?.root_cause || latestResult?.recommendation_summary || exc.description;
  const riskAssessment = latestResult?.risk_assessment;
  const statusKey = (exc.status || '').toLowerCase();
  const isResolved = ['approved', 'rejected', 'closed', 'resolved_auto', 'resolved_human', 'dismissed'].includes(statusKey);
  const excAmount = Number(exc.amount || 0);
  const sevColor = exc.severity === 'critical' || exc.severity === 'CRITICAL' ? 'var(--red)' : exc.severity === 'high' || exc.severity === 'HIGH' ? 'var(--amber)' : exc.severity === 'medium' || exc.severity === 'MEDIUM' ? 'var(--warning)' : 'var(--green)';

  // Extract standards from resolution or results.
  // LLM may return either string[] OR {standard, section, citation}[] --
  // normalize to display strings here so render sites stay simple.
  // (2026-05-22: React #31 crash from rendering objects as JSX children.)
  const _rawStandards: any[] = resolution?.standards_applied
    || (latestResult?.skill_outputs?.standards_cited)
    || [];
  const standards: string[] = _rawStandards.map((s: any) =>
    typeof s === 'string' ? s : (s?.standard ?? s?.section ?? s?.citation ?? JSON.stringify(s))
  );

  // SIL signals for breakdown
  const silSignals = signals.length > 0
    ? signals.map((s: any) => ({ signal_type: s.signal_type, score: s.score, weight: s.weight }))
    : (confidenceBreakdown?.breakdown
        ? Object.entries(confidenceBreakdown.breakdown).map(([k, v]: [string, any]) => ({ signal_type: k, score: v?.score || v, weight: v?.weight }))
        : []);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div className="bc" style={{ marginBottom: 16 }}>
        <span className="bc-link" onClick={onBack}>Activity Tracker</span>
        <span className="bc-sep">&#9658;</span>
        <span className="bc-cur">Exception #{id}</span>
      </div>

      {/* Header Card */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20, border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
              {(() => {
                const isEngine = (t: string) => /workflow skill:|Matching complete:|Detected by workflow|exceptions out of|records scanned|Workflow detection:/i.test(t);
                const fmtType = (t: string) => titleCase(t) || `Exception #${id}`;
                return (exc.title && !isEngine(exc.title)) ? exc.title : fmtType(exc.exception_type);
              })()}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ color: deptColor, fontSize: 11, fontWeight: 600 }}>{mappedDept}</span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>|</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{titleCase(exc.exception_type)}</span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>|</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: sevColor, textTransform: 'uppercase' }}>{exc.severity || 'N/A'}</span>
              {exc.escalation_level && (
                <>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>|</span>
                  <span style={{ fontSize: 9, padding: '1px 6px', background: '#f5f3ff', color: 'var(--navy-800)', borderRadius: 3, fontWeight: 700 }}>{exc.escalation_level}</span>
                </>
              )}
              {exc.source_workflow_instance_id && (
                <>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>|</span>
                  <span style={{ fontSize: 9, padding: '1px 6px', background: 'rgba(139,92,246,0.12)', color: 'var(--navy-800)', borderRadius: 3, fontWeight: 700 }}>
                    Workflow #{exc.source_workflow_instance_id}
                  </span>
                  {exc.source_skill_code && (
                    <span style={{ fontSize: 9, color: 'var(--text-tertiary)' }}>
                      {titleCase(exc.source_skill_code)}
                    </span>
                  )}
                </>
              )}
            </div>
            {exc.description && !/workflow skill:|Matching complete:|Detected by workflow|exceptions out of|records scanned/i.test(exc.description) && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 700 }}>{exc.description}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', minWidth: 160 }}>
            <div className="mono" style={{ fontSize: 22, fontWeight: 600 }}>{formatAmount(excAmount)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
              <StatusDot status={exc.status} size={8} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>{titleCase(exc.status)}</span>
            </div>
            {confidenceScore != null && (
              <div className="mono" style={{ fontSize: 14, fontWeight: 600, marginTop: 6, color: confidenceScore >= 0.85 ? 'var(--green)' : confidenceScore >= 0.6 ? 'var(--amber)' : 'var(--text-tertiary)' }}>
                {(confidenceScore * 100).toFixed(0)}% confidence
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-Column Layout: Details + Confidence */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Details Card */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Details</div>
          <DetailRow label="Exception ID" value={`#${exc.id}`} />
          <DetailRow label="Reference" value={exc.exception_ref || '--'} />
          <DetailRow label="Entity" value={exc.jurisdiction || '--'} />
          <DetailRow label="Vendor" value={exc.vendor_name || '--'} />
          <DetailRow label="ERP Source" value={exc.erp_source || '--'} />
          <DetailRow label="ERP Document" value={exc.erp_doc_id || '--'} />
          <DetailRow label="Resolution Status" value={exc.resolution_status || '--'} />
          {exc.source_workflow_instance_id && (
            <DetailRow label="Source Workflow" value={`#${exc.source_workflow_instance_id}`} />
          )}
          {exc.source_skill_code && (
            <DetailRow label="Source Skill" value={titleCase(exc.source_skill_code)} />
          )}
          {exc.source_transaction_id && (
            <DetailRow label="Transaction ID" value={exc.source_transaction_id} />
          )}
          <DetailRow label="Created" value={exc.created_at ? new Date(exc.created_at).toLocaleString() : '--'} />
          {exc.resolved_at && <DetailRow label="Resolved" value={new Date(exc.resolved_at).toLocaleString()} />}
          {exc.sla_deadline && <DetailRow label="SLA Deadline" value={new Date(exc.sla_deadline).toLocaleString()} />}
        </div>

        {/* Confidence + Risk Card */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Confidence & Risk</div>
          {confidenceBreakdown ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>SIL Composite Score</span>
                <span className="mono" style={{ fontSize: 18, fontWeight: 600, color: confidenceBreakdown.overall_score >= 0.85 ? 'var(--green)' : confidenceBreakdown.overall_score >= 0.6 ? 'var(--amber)' : 'var(--red)' }}>
                  {(confidenceBreakdown.overall_score * 100).toFixed(0)}%
                </span>
              </div>
              <div style={{ height: 8, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${confidenceBreakdown.overall_score * 100}%`, height: '100%', background: confidenceBreakdown.overall_score >= 0.85 ? 'var(--green)' : confidenceBreakdown.overall_score >= 0.6 ? 'var(--amber)' : 'var(--red)', borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-tertiary)', marginTop: 4 }}>{confidenceBreakdown.signal_count || 0} SIL signals evaluated</div>
            </div>
          ) : (
            <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
              {exc.confidence != null ? `Baseline estimate: ${(exc.confidence * 100).toFixed(0)}% -- insufficient data for calibrated score` : 'Confidence score pending'}
            </div>
          )}
          {riskData?.risk && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Risk Score</span>
                <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: riskData.risk.score < 25 ? 'var(--green)' : riskData.risk.score < 50 ? 'var(--amber)' : 'var(--red)' }}>
                  {riskData.risk.score.toFixed(0)} <span style={{ fontSize: 10 }}>{riskData.risk.level}</span>
                </span>
              </div>
              <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${riskData.risk.score}%`, height: '100%', background: riskData.risk.score < 25 ? 'var(--green)' : riskData.risk.score < 50 ? 'var(--amber)' : 'var(--red)', borderRadius: 3 }} />
              </div>
            </div>
          )}
          {riskData?.routing && (
            <div style={{ padding: '8px 10px', background: riskData.routing.action === 'AUTO_RESOLVE' ? 'rgba(16,185,129,0.08)' : 'var(--accent-subtle)', borderRadius: 6, fontSize: 10 }}>
              <span style={{ fontWeight: 700, color: riskData.routing.action === 'AUTO_RESOLVE' ? 'var(--green)' : 'var(--navy-800)' }}>
                {titleCase(riskData.routing.action)}
              </span>
              <span style={{ color: 'var(--text-tertiary)', marginLeft: 8 }}>
                {riskData.routing.requires_human ? 'Human approval needed' : 'Auto-resolvable'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Resolution Mode Badge */}
      {resolution?.resolution_mode && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            background: resolution.resolution_mode === 'MODE_1_ENRICHED' ? 'rgba(111,66,193,0.12)' : resolution.resolution_mode?.startsWith('MODE_2') ? 'rgba(59,130,246,0.12)' : 'rgba(0,0,0,0.06)',
            color: resolution.resolution_mode === 'MODE_1_ENRICHED' ? 'var(--navy-800)' : resolution.resolution_mode?.startsWith('MODE_2') ? 'var(--navy-700)' : 'var(--text-tertiary)',
          }}>
            {resolution.resolution_mode === 'MODE_1_ENRICHED' ? 'AI-Enhanced' : resolution.resolution_mode?.startsWith('MODE_2') ? 'AI-Generated' : 'Template'}
          </span>
        </div>
      )}

      {/* Section 1: AI Narrative */}
      {(() => {
        const narrative = resolution?.diagnosis?.narrative || resolution?.diagnosis?.summary;
        if (!narrative) return null;
        return (
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20, border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.8 }}>{narrative}</div>
          </div>
        );
      })()}

      {/* Section 2: Root Cause */}
      {(() => {
        const rc = resolution?.diagnosis?.root_cause_detail || resolution?.diagnosis?.root_cause || '';
        if (!rc || /workflow skill:|Matching complete:|Detected by workflow/i.test(rc)) return null;
        return (
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Root Cause</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{rc}</div>
          </div>
        );
      })()}

      {/* Section 3: Account Impact */}
      {resolution?.account_impact_analysis && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Account Impact</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{resolution.account_impact_analysis}</div>
        </div>
      )}

      {/* Section 4: Standards Applied */}
      {standards.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {standards.map((s: string, i: number) => (
              <span key={i} style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 4, color: 'var(--navy-700)', fontWeight: 600 }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Section 5: Financial Impact */}
      {resolution?.financial_impact && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--red)', marginBottom: 4 }}>IF UNRESOLVED</div>
            <div style={{ fontSize: 11, color: 'rgba(252,165,165,0.9)', lineHeight: 1.5 }}>{resolution.financial_impact.if_unresolved}</div>
          </div>
          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>IF RESOLVED</div>
            <div style={{ fontSize: 11, color: 'rgba(110,231,183,0.9)', lineHeight: 1.5 }}>{resolution.financial_impact.if_resolved}</div>
          </div>
        </div>
      )}

      {/* Audit Findings (6-point check) */}
      {resolutionAudit && resolutionAudit.finding_count > 0 && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20, border: resolutionAudit.critical_count > 0 ? '1px solid #fecaca' : '1px solid var(--border-default)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Audit Findings</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: resolutionAudit.approve_allowed ? 'rgba(251,191,36,0.15)' : '#fef2f2', color: resolutionAudit.approve_allowed ? 'var(--warning)' : 'var(--negative)' }}>
              {resolutionAudit.approve_allowed ? 'REVIEW REQUIRED' : 'APPROVAL BLOCKED'}
            </span>
            {resolutionAudit.critical_count > 0 && <span style={{ fontSize: 9, color: 'var(--negative)', fontWeight: 700 }}>{resolutionAudit.critical_count} CRITICAL</span>}
            {resolutionAudit.high_count > 0 && <span style={{ fontSize: 9, color: 'var(--warning)', fontWeight: 700 }}>{resolutionAudit.high_count} HIGH</span>}
          </div>
          {(resolutionAudit.findings || []).map((f: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 8px', marginBottom: 2, borderRadius: 4, background: f.severity === 'CRITICAL' ? '#fef8f8' : 'transparent' }}>
              <span style={{ fontSize: 8, fontWeight: 600, minWidth: 55, color: f.severity === 'CRITICAL' ? 'var(--negative)' : f.severity === 'HIGH' ? 'var(--warning)' : 'var(--text-tertiary)' }}>{f.severity}</span>
              <span style={{ fontSize: 8, fontWeight: 700, minWidth: 80, color: 'var(--text-tertiary)' }}>{titleCase(f.check)}</span>
              <span style={{ fontSize: 9, color: 'var(--text-secondary)', flex: 1 }}>{f.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Resolution Options with JEs */}
      {resolution?.options && resolution.options.length > 0 ? (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Resolution Options</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {resolution.options.map((opt: any) => {
              const isRecommended = opt.recommended;
              const isExecuting = executingOption === opt.id;
              const actionType = opt.action_type || (opt.escalate_to ? 'ESCALATE' : 'APPROVE');
              const needsAuthority = ['APPROVE', 'WRITE_OFF', 'ACCRUE'].includes(actionType);
              const auditBlocked = resolutionAudit && !resolutionAudit.approve_allowed;
              const userCanApprove = canApprove(excAmount);
              const isBlocked = needsAuthority && (!userCanApprove || auditBlocked);
              const requiredLevel = getRequiredLevel(excAmount);

              return (
                <div key={opt.id} style={{
                  padding: 14, borderRadius: 10,
                  background: isRecommended ? 'rgba(16,185,129,0.04)' : 'var(--bg-elevated)',
                  border: isRecommended ? '1px solid rgba(16,185,129,0.2)' : '1px solid var(--border-default)',
                }}>
                  {/* Option header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: isRecommended ? 'var(--green)' : 'var(--text-secondary)' }}>{opt.id}</span>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>{opt.label}</span>
                      {isRecommended && <span style={{ fontSize: 9, padding: '2px 8px', background: 'rgba(16,185,129,0.1)', color: 'var(--green)', borderRadius: 4, fontWeight: 700 }}>RECOMMENDED</span>}
                      <span style={{ fontSize: 8, padding: '2px 6px', background: actionType === 'ESCALATE' ? 'rgba(139,92,246,0.15)' : actionType === 'INVESTIGATE' ? 'rgba(251,191,36,0.12)' : 'rgba(16,185,129,0.12)', color: actionType === 'ESCALATE' ? 'var(--navy-800)' : actionType === 'INVESTIGATE' ? 'var(--warning)' : 'var(--green)', borderRadius: 3, fontWeight: 700 }}>{actionType}</span>
                    </div>
                    {opt.confidence != null && (
                      <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: opt.confidence >= 0.8 ? 'var(--green)' : opt.confidence >= 0.5 ? 'var(--amber)' : 'var(--text-tertiary)' }}>
                        {(opt.confidence * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {opt.description && <div style={{ fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 8 }}>{opt.description}</div>}
                  {opt.validation_warnings?.length > 0 && (
                    <div style={{ padding: 8, background: 'rgba(251,191,36,0.06)', borderRadius: 4, marginBottom: 8 }}>
                      {opt.validation_warnings.map((w: string, wi: number) => (
                        <div key={wi} style={{ fontSize: 9, color: 'var(--warning)' }}>Warning: {w}</div>
                      ))}
                    </div>
                  )}
                  {/* Journal Entries */}
                  {opt.journal_entries?.length > 0 && (
                    <div style={{ padding: 10, background: 'rgba(0,0,0,0.15)', borderRadius: 6, marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>JOURNAL ENTRIES</div>
                      {opt.journal_entries[0]?.type === 'NO_ADJUSTMENT' ? (
                        <div style={{ fontSize: 11, color: 'var(--navy-700)', lineHeight: 1.6, padding: '6px 10px', background: 'rgba(59,130,246,0.06)', borderRadius: 4, borderLeft: '2px solid var(--accent-border)' }}>
                          {opt.journal_entries[0].reason || 'No adjustment required'}
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                <th style={{ padding: '4px 8px', textAlign: 'left', fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 700 }}>Account</th>
                                <th style={{ padding: '4px 8px', textAlign: 'left', fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 700 }}>Name</th>
                                <th style={{ padding: '4px 8px', textAlign: 'right', fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 700 }}>Debit</th>
                                <th style={{ padding: '4px 8px', textAlign: 'right', fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 700 }}>Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {opt.journal_entries.map((je: any, j: number) => (
                                <tr key={j} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                  <td style={{ padding: '5px 8px', color: 'var(--navy-700)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {je.debit_account || je.credit_account || je.account || '\u2014'}
                                  </td>
                                  <td style={{ padding: '5px 8px', color: 'var(--text-secondary)' }}>{je.account_name || '\u2014'}</td>
                                  <td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--navy-700)' }}>
                                    {(je.debit != null && Number(je.debit) > 0) ? Number(je.debit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '\u2014'}
                                  </td>
                                  <td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--positive)' }}>
                                    {(je.credit != null && Number(je.credit) > 0) ? Number(je.credit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '\u2014'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Action buttons */}
                  {!isResolved && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      {isBlocked && (
                        <span style={{ fontSize: 9, color: auditBlocked ? 'var(--negative)' : 'var(--warning)' }}>
                          {auditBlocked ? `Blocked: ${resolutionAudit?.critical_count || 0} critical finding(s)` : `Requires ${requiredLevel} (your level: ${role.level})`}
                        </span>
                      )}
                      {actionType === 'ESCALATE' ? (
                        <ActionButton
                          label={opt.escalate_to ? `ESCALATE TO ${opt.escalate_to}` : 'ESCALATE'}
                          onClick={() => handleEscalate(opt.escalate_to)}
                          variant="secondary"
                          size="sm"
                          disabled={!!executingOption}
                        />
                      ) : (
                        <>
                          <ActionButton
                            label={isExecuting ? 'PROCESSING...' : actionType === 'WRITE_OFF' ? 'WRITE OFF' : actionType === 'ACCRUE' ? 'ACCRUE' : 'APPROVE & POST'}
                            onClick={() => executeResolution(opt.id, 'approved', opt.label)}
                            variant="success"
                            size="sm"
                            disabled={!!executingOption || isBlocked}
                          />
                          <ActionButton
                            label="REJECT"
                            onClick={() => executeResolution(opt.id, 'rejected', opt.label)}
                            variant="danger"
                            size="sm"
                            disabled={!!executingOption}
                          />
                          {isBlocked && (
                            <ActionButton
                              label="ESCALATE"
                              onClick={() => handleEscalate()}
                              variant="secondary"
                              size="sm"
                              disabled={!!executingOption}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : !isResolved && !resolution ? (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20, textAlign: 'center' }}>
          {loadingResolution ? (
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Generating AI Resolution...</div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Diagnosing root cause, calculating financial impact, generating options</div>
            </div>
          ) : (
            <ActionButton
              label="GENERATE AI RESOLUTION"
              onClick={generateResolution}
              variant="primary"
            />
          )}
        </div>
      ) : null}

      {/* Section 8: Corrective Actions */}
      {resolution?.corrective_actions_detail && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Corrective Actions</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{resolution.corrective_actions_detail}</div>
        </div>
      )}

      {/* Standalone escalate */}
      {!isResolved && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 20 }}>
          <ActionButton
            label="ESCALATE TO NEXT LEVEL"
            onClick={() => handleEscalate()}
            variant="secondary"
            size="sm"
          />
        </div>
      )}

      {/* Resolve feedback */}
      {resolveStatus && (
        <div style={{ marginBottom: 20, padding: '12px 16px', background: resolveStatus.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${resolveStatus.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 8, textAlign: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: resolveStatus.ok ? 'var(--green)' : 'var(--red)' }}>
            {resolveStatus.ok ? 'SUCCESS' : 'ERROR'}: {resolveStatus.msg}
          </span>
        </div>
      )}

      {/* Resolution status (if already resolved) */}
      {exc.resolution_action && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20, border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>RESOLVED</div>
          {exc.resolution_summary && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{exc.resolution_summary}</div>}
          {exc.human_notes && <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4, fontStyle: 'italic' }}>{exc.human_notes}</div>}
        </div>
      )}

      {/* SIL Breakdown */}
      {silSignals.length > 0 && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <SILBreakdown
            signals={silSignals}
            composite={confidenceBreakdown?.overall_score || exc.confidence || 0}
            risk={riskData?.risk?.score}
          />
        </div>
      )}

      {/* Follow-Up Tasks */}
      {resolution?.follow_up_tasks?.length > 0 && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Follow-Up Tasks</div>
          {resolution.follow_up_tasks.map((t: any, i: number) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 10, flex: 1 }}>{t.task}</span>
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', marginLeft: 12 }}>{t.owner}{(t.due || t.deadline) ? ` | ${t.due || t.deadline}` : ''}</span>
            </div>
          ))}
        </div>
      )}

      {/* Audit Trail (SOX Compliant) */}
      {auditTrail.length > 0 && (
        <div className="card" style={{ padding: '16px 20px' }}>
          <AuditTrail entries={auditTrail} />
        </div>
      )}
    </div>
  );
}
